import { useEffect, useMemo } from 'react'
import { z } from 'zod'
import { useLocalStorage } from 'usehooks-ts'
import { useIsMobile } from '@/hooks/use-mobile'
import morpheusApi from '../morpheus.slice'

export const userConfigDefault = z.object({
  dark_mode: z.boolean().default(false),
  use_system_colour_mode: z.boolean().default(true),
  action_bar: z
    .object({
      icon: z.boolean().default(true),
      label: z.boolean().default(false),
      draggable: z.boolean().default(true),
      position: z.enum(['top', 'bottom', 'left', 'right']).default('left'),
      vertical: z.boolean().default(false),
      has_updated: z.boolean().default(true),
      is_fixed: z.boolean().default(false),
    })
    .default({
      icon: true,
      label: false,
      draggable: false,
      position: 'left',
      has_updated: true,
      vertical: false,
      is_fixed: true,
    }),
})

function getDefaults<Schema extends typeof userConfigDefault>(schema: Schema) {
  return Object.fromEntries(
    Object.entries(schema.def.shape).map(([key, value]: [string, any]) => {
      if (value.type === 'default' && value.def?.defaultValue !== undefined) {
        return [key, value.def.defaultValue]
      }
      return [key, undefined]
    })
  )
}

export type UserConfigType = z.infer<typeof userConfigDefault>

const userConfigItem = getDefaults(userConfigDefault) as UserConfigType

export const useUserConfig = () => {
  const { data: initData = userConfigItem, isSuccess } =
    morpheusApi.useGetUserConfigQuery(null, {
      skipPollingIfUnfocused: true,
      pollingInterval: 1000 * 60 * 5,
    })

  const [data, setData] = useLocalStorage<UserConfigType>(
    'user-config',
    initData
  )

  useEffect(() => {
    if (!initData) return
    setData(initData)
  }, [initData])

  // ⚠️ Threshold hook, NOT a live width. `useUserConfig` is called by
  // DashLayout (the app shell), so per-resize width state here re-rendered
  // the whole app during window drags (the app-wide resize flicker).
  // `useIsMobile` (768px, vs the old 764) only updates when crossed.
  const isMobile = useIsMobile()

  // Ensure data has all required properties by parsing it through the schema
  const safeData = useMemo(() => {
    try {
      return userConfigDefault.parse(data)
    } catch {
      return userConfigItem
    }
  }, [data])

  const resData = useMemo(() => {
    if (isMobile) {
      return {
        ...safeData,
        isSuccess,
        action_bar: {
          ...safeData.action_bar,
          position: 'bottom',
          is_fixed: true,
          icon: true,
          label: true,
          vertical: false,
        },
      }
    }

    if (['left', 'right'].includes(safeData.action_bar.position)) {
      return {
        ...safeData,
        isSuccess,
        action_bar: {
          ...safeData.action_bar,
          vertical: true,

          position: safeData.action_bar.position,
        },
      }
    }
    return {
      ...safeData,
      isSuccess,
      action_bar: {
        ...safeData.action_bar,
        vertical: ['left', 'right'].includes(safeData.action_bar.position),

        position: safeData.action_bar.position,
      },
    }
  }, [safeData, isSuccess, isMobile])

  return resData
}
